/**
 * A round button whose only content is an icon — zoom, filter, full screen,
 * close.
 *
 * The anatomy the Vesper canvas draws for the globe controls: a circle, the
 * icon centred, no label. Because nothing visible names it, `label` is
 * required and becomes the accessible name.
 *
 *   variant  quiet     transparent until hovered, for a surface that has its own fill
 *            floating  a translucent wash, for floating over the globe, a photo or
 *                      a translucent bar, where the scene should show through;
 *                      in the accent while the popover it opened is open
 *                      (`aria-expanded`)
 *   size     sm 28px | md 32px
 *   tip      the side its label shows on, in a Tooltip, while it is hovered or
 *            has keyboard focus — for a button floating on its own, where the
 *            icon is all anyone sees. Not while it rests or while its popover
 *            is open, which takes that side; Escape puts it away, through the
 *            shared stack, before anything under it.
 *
 * `reason` rests it, as it rests a Button: the disabled look, the pointer and
 * focus kept, the press swallowed, and a hover card naming the button and
 * saying why — which assistive technology reads as its description — in place
 * of the tip. A Stepper's plus rests this way at its most.
 *
 * `variant` is the treatment, as on Button. There is no colour tone: an icon
 * button is never the decisive action.
 *
 * The icon is an `Icon`, which paints in `currentColor`, so the variant colours it.
 */

import { useCallback, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, FocusEvent, MouseEvent, PointerEvent, ReactNode, Ref } from 'react';

import type { Side } from '../../utils/clamp-to-viewport';
import { HoverCard, HoverCardNote, HoverCardTitle } from './hover-card';
import { Tooltip } from './tooltip';
import { useModalBehavior } from './use-modal-behavior';
import styles from './icon-button.module.css';

const VARIANT = {
  quiet: styles.quiet,
  floating: styles.floating,
} as const;

const SIZE = {
  sm: styles.sm,
  md: styles.md,
} as const;

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Required: the button shows no text, so this is its only name. */
  label: string;
  variant?: keyof typeof VARIANT;
  size?: keyof typeof SIZE;
  /** The side the label shows on while hovered or focused from the keyboard. */
  tip?: Side;
  /** Why it cannot act now: it rests, and says so in a hover card. */
  reason?: string;
  /** The icon: an `Icon`, which takes the variant's colour. */
  children: ReactNode;
  /** For a Popover or HoverCard that hangs from this button. */
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({
  label,
  variant = 'quiet',
  size = 'md',
  tip,
  reason,
  className = '',
  type = 'button',
  children,
  ref,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
  onClick,
  ...rest
}: IconButtonProps) {
  const own = useRef<HTMLButtonElement | null>(null);
  // The caller's ref and this button's own, which the tip hangs from.
  const setRef = useCallback(
    (element: HTMLButtonElement | null) => {
      own.current = element;
      if (typeof ref === 'function') ref(element);
      else if (ref) ref.current = element;
    },
    [ref],
  );

  const [tipShown, setTipShown] = useState(false);
  const hideTip = useCallback(() => setTipShown(false), []);
  // Escape takes the tip away first, as it does a hover card. The tip's own
  // element is the layer the hook ranks, as every other's is.
  const tipRef = useModalBehavior<HTMLDivElement>({ isOpen: tipShown, onDismiss: hideTip, lockBackgroundScroll: false, trapFocus: false });
  const showTip = (button: HTMLButtonElement) => {
    if (button.disabled || button.getAttribute('aria-expanded') === 'true') return;
    setTipShown(true);
  };

  // Only a button with a tip listens for it; a resting one shows its reason instead.
  const tipHandlers = tip && !reason
    ? {
        onPointerEnter: (event: PointerEvent<HTMLButtonElement>) => {
          onPointerEnter?.(event);
          showTip(event.currentTarget);
        },
        onPointerLeave: (event: PointerEvent<HTMLButtonElement>) => {
          onPointerLeave?.(event);
          hideTip();
        },
        onFocus: (event: FocusEvent<HTMLButtonElement>) => {
          onFocus?.(event);
          // Keyboard focus only: a press focuses the button too, and the
          // pointer is already showing the label, or has moved on.
          if (event.currentTarget.matches(':focus-visible')) showTip(event.currentTarget);
        },
        onBlur: (event: FocusEvent<HTMLButtonElement>) => {
          onBlur?.(event);
          hideTip();
        },
        onClick: (event: MouseEvent<HTMLButtonElement>) => {
          onClick?.(event);
          // A press opens what the label would cover, or changes what it names.
          hideTip();
        },
      }
    : { onPointerEnter, onPointerLeave, onFocus, onBlur, onClick };

  const button = (
    <button
      ref={setRef}
      type={type}
      aria-label={label}
      className={[styles.iconButton, VARIANT[variant], SIZE[size], className].filter(Boolean).join(' ')}
      {...tipHandlers}
      {...rest}
      // Resting overrides what the caller passed: the press goes nowhere.
      {...(reason ? { 'aria-disabled': true, onClick: undefined } : {})}
    >
      {children}
      {tip && !reason && (
        <Tooltip visible={tipShown} beside={{ anchor: own, side: tip }} layerRef={tipRef}>
          {label}
        </Tooltip>
      )}
    </button>
  );
  if (!reason) return button;
  return (
    <HoverCard
      label={label}
      description={reason}
      placement="below"
      card={
        <>
          <HoverCardTitle>{label}</HoverCardTitle>
          <HoverCardNote>{reason}</HoverCardNote>
        </>
      }
    >
      {button}
    </HoverCard>
  );
}
